import { KeyValuePipe } from '@angular/common';
import {
  Component,
  computed,
  ElementRef,
  inject,
  OnInit,
  signal,
  viewChild,
  input,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatOption } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSelect, MatSelectChange } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { SnakeCaseTitlePipe } from '../../../../shared-lazy/pipes/snakeCaseTitle.pipe';
import { AvatarComponent } from '../../../../shared/components/avatar/avatar.component';
import { DotsLoaderComponent } from '../../../../shared/components/dots-loader/dots-loader.component';
import { TeamMember, UserDetails } from '../../../auth0/models';
import { Auth0Store } from '../../../auth0/store';
import { UserUtils } from '../../../auth0/utils/user.utils';
import { InviteMemberPopupComponent } from '../invite-member-popup/invite-member-popup.component';

@Component({
  selector: 'gnx-members',
  templateUrl: './members.component.html',
  styleUrls: ['./members.component.scss'],
  imports: [DotsLoaderComponent, AvatarComponent, MatSelect, MatOption, KeyValuePipe, SnakeCaseTitlePipe, MatTooltip],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class MembersComponent implements OnInit {
  private dialog = inject(MatDialog);

  protected auth0Store = inject(Auth0Store);
  readonly data = input<UserDetails>(undefined);
  readonly isAdmin = input<boolean>(undefined);
  readonly searchInput = viewChild<ElementRef>('searchInput');

  searchText = signal('');

  teamMembersData = computed(() => {
    const search = this.searchText();
    const members = this.auth0Store.teamMembersData();
    if (!members || !search) {
      return members;
    }
    const lowerSearch = search.toLowerCase();
    return members.filter((user) => user.name?.toLowerCase().includes(lowerSearch) || user.email.includes(lowerSearch));
  });

  readonly USER_TYPE = {
    admin: 'Admin',
    member: 'Member',
  };

  get showApplicativeRoles(): boolean {
    return this.data()?.org_config?.userConfig?.rolesAndPermissionsConfig?.active ?? false;
  }

  ngOnInit() {
    this.auth0Store.getTeamMembers({ extended: true });
    if (this.showApplicativeRoles) {
      this.auth0Store.getApplicativeRoles();
    }
  }

  deleteMember(user: TeamMember) {
    this.auth0Store.deleteMember({
      userId: user.auth0_id,
      email: user.email,
      isPending: user.status.toLowerCase() === 'pending',
      userName: user.name,
    });
  }

  openInviteMemberDialog() {
    this.dialog.open(InviteMemberPopupComponent, {
      data: this.data(),
      width: '660px',
      height: '530px',
      backdropClass: 'error-popup-backdrop',
      panelClass: 'error-popup-panel',
    });
  }

  getOrgMemberRole(user: TeamMember): string {
    return UserUtils.isUserAnAdmin(user) ? this.USER_TYPE.admin : this.USER_TYPE.member;
  }

  onMemberRoleChange(user: TeamMember, event: MatSelectChange) {
    const isPending = user.status.toLowerCase() === 'pending';
    this.auth0Store.setMemberRoles({
      userId: user.auth0_id,
      email: user.email,
      isStatusPending: isPending,
      roles: event.value === this.USER_TYPE.admin ? UserUtils.addAdminRole(user) : UserUtils.removeAdminRole(user),
    });
  }

  onApplicativeRoleChange(user: TeamMember, event: MatSelectChange) {
    const isPending = user.status.toLowerCase() === 'pending';
    this.auth0Store.setMemberApplicativeRole({ email: user.email, role: event.value, isPending });
  }
}
